import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, MessageSquare } from 'lucide-react';
import { useMyBotsQuery, useProjectChatsQuery } from '../../hooks/queries';
import BotAvatar from '../bots/BotAvatar';
import { sidebarChats } from '../../lib/sidebarChats';
import ChatUnreadDot from '../unread/ChatUnreadDot';
import { chatIdFromUrl, chatPath } from '../bots/chatLinks';

/**
 * A project's chats under it in the sidebar. A project that also has lists or
 * documents folds its chats into one "Chats" row, so they never crowd them out;
 * a project with only chats shows them right away (no second click). At most
 * three, then "All chats" opens the project's chat history.
 */
const SidebarProjectChats = ({ project, grouped, needsYou = 0, unread = 0, onItemClick }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const openChatId = chatIdFromUrl(`${location.pathname}${location.search}`);
  const { data } = useProjectChatsQuery(project.id);
  const { data: mine } = useMyBotsQuery();
  const bots = useMemo(() => new Map((mine?.bots || []).map((b) => [b.id, b])), [mine]);
  const chats = useMemo(() => (data?.chats || []).filter((c) => bots.has(c.bot_id)), [data, bots]);
  const shown = sidebarChats(chats);
  const openHere = !!openChatId && chats.some((c) => c.id === openChatId);
  const storageKey = `bb.sidebar.chats.${project.id}`;
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
  });
  const expanded = !grouped || open || openHere;
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch { /* private mode */ }
  };
  if (!chats.length) return null;

  const row = (c) => {
    const active = c.id === openChatId;
    const bot = bots.get(c.bot_id);
    return (
      <Link key={c.id} to={chatPath(c.id)} state={{ chatId: c.id, botId: c.bot_id, projectId: project.id }}
        onClick={onItemClick} data-testid="sidebar-chat"
        className={`flex items-center gap-2 px-2 py-1 rounded-md text-[13px] transition-colors min-w-0 ${
          active ? 'bg-accent text-foreground font-medium' : 'text-foreground/80 hover:text-foreground hover:bg-accent'}`}>
        <BotAvatar bot={bot} size="xs" state={c.working ? 'working' : null} />
        <span className={`truncate flex-1 ${c.unread ? 'font-medium text-foreground' : ''}`}>{c.title || t('bots.chat.new_chat')}</span>
        {c.pending_approvals > 0 ? <span className="w-1.5 h-1.5 rounded-full bg-attention flex-shrink-0" aria-label={t('bots.state.needs_you')} />
          : c.unread && <ChatUnreadDot />}
      </Link>
    );
  };

  const list = (
    <>
      {shown.map(row)}
      {chats.length > shown.length && (
        <Link to={`/app/projects?project=${project.id}&panel=chats`} onClick={onItemClick}
          className="flex items-center px-2 py-1 pl-[34px] rounded-md text-[12.5px] text-muted-foreground hover:text-foreground hover:bg-accent">
          {t('bots.chat.all_chats')}
        </Link>
      )}
    </>
  );

  if (!grouped) return list;
  return (
    <>
      <button type="button" onClick={toggle} aria-expanded={expanded} data-testid="sidebar-chats-group"
        className="w-full flex items-center gap-2 px-2 py-1 rounded-md text-[13px] text-foreground/80 hover:text-foreground hover:bg-accent transition-colors text-left">
        <MessageSquare className="w-3.5 h-3.5 flex-shrink-0 text-brand-icon" />
        <span className="flex-1 truncate">{t('bots.chat.chats_title')}</span>
        {needsYou > 0 ? <span className="w-1.5 h-1.5 rounded-full bg-attention flex-shrink-0" aria-label={t('bots.state.needs_you')} />
          : unread > 0 && <ChatUnreadDot />}
        <span className="text-[11px] text-muted-foreground tabular-nums">{chats.length}</span>
        <ChevronRight className={`w-3 h-3 text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>
      {expanded && <div className="ml-[13px] border-l border-border pl-1.5 space-y-px">{list}</div>}
    </>
  );
};

export default SidebarProjectChats;
